package team.platforma.multiplatform_material_extension.ui.phone

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextField
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import materialextension.multiplatform_material_extension.generated.resources.Res
import materialextension.multiplatform_material_extension.generated.resources.ic24_mel_exit_circle
import materialextension.multiplatform_material_extension.generated.resources.mel_ic_clear
import team.platforma.multiplatform_material_extension.ui.core.InputField
import team.platforma.multiplatform_material_extension.ui.core.InputFieldProperties
import team.platforma.multiplatform_material_extension.ui.core.InputFieldState
import team.platforma.multiplatform_material_extension.ui.core.InputFieldStateListener
import team.platforma.multiplatform_material_extension.ui.core.StyledTextField
import team.platforma.multiplatform_material_extension.ui.core.field_utils.endIcon
import team.platforma.multiplatform_material_extension.ui.core.global_style.area.supportArea
import team.platforma.multiplatform_material_extension.ui.core.global_style.setFiledTextFieldDefaults
import team.platforma.multiplatform_material_extension.ui.core.global_style.setOutlinedTextFieldDefaults
import team.platforma.multiplatform_material_extension.ui.core.global_style.shape.setFilledTextFieldDefaultShape
import team.platforma.multiplatform_material_extension.ui.core.global_style.shape.setOutlineTextFieldDefaultShape
import team.platforma.multiplatform_material_extension.ui.core.rememberInputFieldState
import team.platforma.multiplatform_material_extension.util.forms.FormProperties
import team.platforma.multiplatform_material_extension.util.forms.keyListener
import team.platforma.multiplatform_material_extension.util.forms.rememberForms
import team.platforma.multiplatform_material_extension.util.rememberPhoneValidator
import team.platforma.multiplatform_material_extension.util.rememberStringValidator
import team.platforma.multiplatform_material_extension.util.visual_transformation.MaskVisualTransformation

@Deprecated("")
object PhoneField : InputField {
    @Composable
    fun create(
        phone: String?,
        onPhoneChange: (change: String) -> Unit,
        mask: String = "## (###) ###-##-##",
        maskChar: Char = '#',
        modifier: Modifier? = null,
        required: Boolean = true,
        formProperties: FormProperties? = null,
        error: Boolean = false,
        message: String = ""
    ): StyledTextField {

        val form by rememberForms()

        val mForm = formProperties ?: FormProperties(form, key = "")

        LaunchedEffect(mForm.key) {
            mForm.form.initValue(mForm.key, phone ?: "")
            mForm.form.changeValue(mForm.key, phone ?: "")
        }


        return builder(
            PhoneFieldImpl(
                value = phone,
                mask = mask,
                maskChar = maskChar,
                areaModifier = modifier,
                resultValueChange = { change ->
                    onPhoneChange(change)
                },
                required = required,
                form = mForm,
                error = error,
                message = message
            )
        )
    }

    override fun builder(textFieldImpl: StyledTextField): StyledTextField = textFieldImpl
}

@Composable
private fun PhoneFieldImpl(
    value: String?,
    resultValueChange: (change: String) -> Unit,
    mask: String,
    maskChar: Char,
    areaModifier: Modifier?,
    required: Boolean,
    form: FormProperties,
    error: Boolean,
    message: String
): StyledTextField {
    val inputFieldState = rememberInputFieldState()

    val focusRequester = remember { FocusRequester() }
    val index = remember { form.form.registerFocus(focusRequester) }

    var phoneNumber by remember { mutableStateOf(TextFieldValue(value ?: "")) }

    var showClearIcon by remember { mutableStateOf(false) }

    var isError by remember { mutableStateOf(false) }

    LaunchedEffect(error) {
        isError = error
    }

    var helpMessage by remember { mutableStateOf("") }

    LaunchedEffect(message) {
        helpMessage = message
    }

    LaunchedEffect(phoneNumber) {
        showClearIcon = phoneNumber.text != ""
    }

    val phoneValidator = rememberPhoneValidator()
    val fieldValidator = rememberStringValidator()

    LaunchedEffect(Unit) {
        form.form.changeValue(form.key, phoneNumber.text)
        val validPhone =
            phoneValidator.createValidatePhoneResult(required, phoneNumber.text)
        val maskLength = mask.count { it == maskChar }
        val validatePhoneLength = fieldValidator.createResultValidation(
            required,
            validPhone,
            phoneNumber.text,
            maskLength
        )
        form.form.setValidateState(form.key, validatePhoneLength)
    }

    val coroutineScope = rememberCoroutineScope()


    DisposableEffect(inputFieldState) {

        val listener = object : InputFieldStateListener {

            override fun onEndIconClick() {
                resultValueChange("")

                form.form.changeValue(form.key, "")
                form.form.setValidateState(form.key, !required)

                coroutineScope.launch {
                    while (phoneNumber.text.isNotEmpty()) {
                        phoneNumber = TextFieldValue(
                            text = phoneNumber.text.dropLast(1),
                            selection = TextRange(phoneNumber.text.length - 1)
                        )
                        delay(20)
                    }
                }
            }

            override fun onValueChange(textFieldValue: TextFieldValue) {
                helpMessage = ""
                isError = false
                phoneNumber = textFieldValue

                form.form.changeValue(form.key, phoneNumber.text)

                val validPhone =
                    phoneValidator.createValidatePhoneResult(required, phoneNumber.text)
                val maskLength = mask.count { it == maskChar }
                val validatePhoneLength = fieldValidator.createResultValidation(
                    required,
                    validPhone,
                    phoneNumber.text,
                    maskLength
                )
                form.form.setValidateState(form.key, validatePhoneLength)

                resultValueChange(phoneNumber.text)
            }

        }

        inputFieldState.addListener(listener)

        onDispose {
            inputFieldState.removeListener(listener)
        }
    }


    return object : StyledTextField {
        @Composable
        override fun Outline(properties: InputFieldProperties?) {
            Column(
                modifier = (areaModifier ?: Modifier).width(IntrinsicSize.Min)
            ) {
                OutlinedTextField(
                    value = phoneNumber,
                    onValueChange = { change ->
                        changePhoneValue(
                            phoneNumber = phoneNumber,
                            state = inputFieldState,
                            change = change,
                            mask = mask,
                            maskChar = maskChar
                        )

                    },
                    trailingIcon = {
                        if (showClearIcon) {
                            endIcon(Res.drawable.ic24_mel_exit_circle, inputFieldState)
                        }
                    },
                    visualTransformation = MaskVisualTransformation(mask, maskChar),
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
                    label = properties?.label,
                    placeholder = properties?.placeholder,
                    modifier = (properties?.modifier ?: Modifier)
                        .keyListener(Key.Tab) {
                            form.form.moveToNextFocus(index)
                        }.focusRequester(focusRequester),
                    shape = properties?.shape ?: setOutlineTextFieldDefaultShape(),
                    colors = properties?.colors ?: setOutlinedTextFieldDefaults(),
                    isError = isError
                )
                Box(
                    modifier = Modifier.heightIn(min = supportArea()).fillMaxWidth()
                ){
                    if (helpMessage.isNotEmpty()) {
                        val helperTextColors = properties?.colors ?: setOutlinedTextFieldDefaults()
                        Text(
                            modifier = Modifier.padding(horizontal = 16.dp),
                            text = helpMessage,
                            color = if (isError) helperTextColors.errorSupportingTextColor else helperTextColors.focusedSupportingTextColor,
                            style = MaterialTheme.typography.bodySmall
                        )
                    }
                }
            }
        }

        @Composable
        override fun Filled(properties: InputFieldProperties?) {
            Column(
                modifier = (areaModifier ?: Modifier).width(IntrinsicSize.Min)
            ) {
                TextField(
                    value = phoneNumber,
                    onValueChange = { change ->
                        changePhoneValue(
                            phoneNumber = phoneNumber,
                            state = inputFieldState,
                            change = change,
                            mask = mask,
                            maskChar = maskChar
                        )
                    },
                    trailingIcon = {
                        if (showClearIcon) {
                            endIcon(Res.drawable.ic24_mel_exit_circle, inputFieldState)
                        }
                    },
                    visualTransformation = MaskVisualTransformation(mask, maskChar),
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
                    label = properties?.label,
                    placeholder = properties?.placeholder,
                    modifier = (properties?.modifier ?: Modifier)
                        .keyListener(Key.Tab) {
                            form.form.moveToNextFocus(index)
                        }.focusRequester(focusRequester),
                    shape = properties?.shape ?: setFilledTextFieldDefaultShape(),
                    colors = properties?.colors ?: setFiledTextFieldDefaults(),
                    isError = isError
                )
                Box(
                    modifier = Modifier.heightIn(min = supportArea()).fillMaxWidth()
                ){
                    if (helpMessage.isNotEmpty()) {
                        val helperTextColors = properties?.colors ?: setOutlinedTextFieldDefaults()
                        Text(
                            modifier = Modifier.padding(horizontal = 16.dp),
                            text = helpMessage,
                            color = if (isError) helperTextColors.errorSupportingTextColor else helperTextColors.focusedSupportingTextColor,
                            style = MaterialTheme.typography.bodySmall
                        )
                    }
                }
            }
        }
    }
}

private fun changePhoneValue(
    phoneNumber: TextFieldValue,
    state: InputFieldState,
    change: TextFieldValue,
    mask: String,
    maskChar: Char,
) {
    var changeText = change.text.take(mask.count { it == maskChar })

    val cursorPosition = change.selection.end
    var changeResult = TextFieldValue(
        text = "",
        selection = TextRange(minOf(0, changeText.length))
    )

    if (phoneNumber.text.isEmpty()) {
        when {
            changeText.startsWith("+", 0) -> {
                changeResult = TextFieldValue(
                    text = changeText,
                    selection = TextRange(minOf(cursorPosition, changeText.length))
                )

            }

            changeText.startsWith("7", 0) -> {
                changeText = "+$changeText"
                changeResult = TextFieldValue(
                    text = changeText,
                    selection = TextRange(minOf(cursorPosition + 1, changeText.length))
                )
            }

            changeText.startsWith("8", 0) -> {
                changeText = changeText.replaceFirst("8", "+7")
                changeResult = TextFieldValue(
                    text = changeText,
                    selection = TextRange(minOf(cursorPosition + 1, changeText.length))
                )
            }

            changeText.startsWith("9", 0) -> {
                changeText = "+7$changeText"
                changeResult = TextFieldValue(
                    text = changeText,
                    selection = TextRange(minOf(cursorPosition + 2, changeText.length))
                )
            }

            Regex("^[0-6]").containsMatchIn(changeText) -> {
                changeResult = TextFieldValue(
                    text = changeText,
                    selection = TextRange(minOf(cursorPosition, changeText.length))
                )
            }

        }
    } else {
        if (changeText.matches(Regex("^[\\d+]+$"))) {
            changeResult = TextFieldValue(
                text = if (phoneNumber.text.length != changeText.length) changeText else phoneNumber.text,
                selection = TextRange(minOf(cursorPosition, changeText.length))
            )
        } else if (changeText.isEmpty()) {
            changeResult = TextFieldValue(
                text = "",
                selection = TextRange(minOf(0, changeText.length))
            )
        }
    }
    state.changeValue(changeResult)
}